'use strict';

export default angular
  .module('maestro.services.data.locations', [
    'maestro.services.utils',
    'maestro.services.webcore'
  ])
  .service('LocationsService', LocationsService)
  .name;

function LocationsService(webcore,
                          $q,
                          utils,
                          CacheService) {

  const COUNTRIES_LOCAL_STORAGE_KEY = 'Main-Countries';

  /**
   * Gets list of supported countries.
   */
  this.getCountries = () => {
    let data = utils.getStorageItem(COUNTRIES_LOCAL_STORAGE_KEY);
    if (data) {
      return $q.resolve(data);
    }
    return webcore.services.location.getCountries()
      .then(data => {
          utils.setStorageItem(COUNTRIES_LOCAL_STORAGE_KEY, data);
          return $q.resolve(data);
        },
        err => $q.reject(err));
  };

  /**
   * Gets specified location details.
   * @param {number} organizationId
   * @param {number} locationId
   * @param {boolean} tree
   * @returns {Promise}
   */
  this.getLocationInfo = (organizationId, locationId, tree = true) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }

    return this.getLocations(
      organizationId, {
        locationId: locationId,
        tree: tree
      }
    );
  };

  /**
   * Get locations via Administrative API
   * @returns {Promise}
   */
  this.getLocations = (organizationId, params) => {
    // Copy to reduce angular-related issues
    params = angular.copy(params);

    if (organizationId) {
      params.organizationId = organizationId;
    }

    // Delete undefined values
    Object.keys(params)
      .forEach(key => params[key] == null && delete params[key]);

    // Apply default values
    params = {
      sortCollection: 'locations',
      sortBy: 'creationDate',
      sortOrder: 'desc',
      getTags: true,
      tree: true,

      ...params,
    };

    return $q.when(
      webcore.services.organization.getOrganizationLocations(params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Update location in organization
   * @param {number} organizationId
   * @param {number} locationId
   * @param {{location: Object}} editLocationModel
   * @return {Promise}
   */
  this.updateLocation = (organizationId, locationId, editLocationModel) => {
    return $q.when(
      webcore.services.location.updateLocationInOrganization(organizationId, locationId, editLocationModel)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Manage single location in organization
   * @param {number} organizationId
   * @param {number} locationId
   * @param {Object} params
   * @return {Promise}
   */
  this.manageSingleLocation = (organizationId, locationId, params) => {
    let locations = locationId && locationId > 0 ? [locationId] : [];
    return this.manageMultipleLocations(organizationId, locations, params);
  };

  /**
   * Manage multiple locations in organization
   * @param {number} organizationId
   * @param {Array<number>} locations array of location ids
   * @param {Object} params update parameters
   * @param {string} [params.notes] notes
   * @param {boolean} [params.delete]
   * @return {Promise}
   */
  this.manageMultipleLocations = (organizationId, locations, params) => {
    if (!organizationId || organizationId < 1 || isNaN(organizationId)) {
      return $q.reject('Organization ID is not specified!');
    }
    if (!locations || locations.length === 0) {
      return $q.reject('Location IDs is not specified!');
    }
    let locationsModel = params || {};
    locationsModel.locationIds = locations;
    if (!params || !params.delete) {
      locationsModel.delete = false;
    }
    return $q.when(
      webcore.services.location.manageMultipleLocations(organizationId, locationsModel)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Create location in organization
   * @param {number} organizationId
   * @param {*} locationModel
   * @param {{groupId: number, parentId: number, userId: number}} [params]
   * @return {Promise}
   */
  this.createLocation = (organizationId, locationModel, params) => {
    return $q.when(
      webcore.services.location.createLocationInOrganization(organizationId, locationModel, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Delete location from organization.
   * @param {number} organizationId ID of the organization to delete location from.
   * @param {number} locationId ID of the location to delete.
   * @return {Promise}
   */
  this.deleteLocation = (organizationId, locationId) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }

    return $q.when(
      webcore.services.location.deleteLocationInOrganization(organizationId, { locationId: locationId })
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets location users
   * @param {number} locationId ID of the location to get users from
   * @returns {Promise}
   */
  this.getLocationUsers = (locationId) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }

    return $q.when(
      webcore.services.location.getLocationUsers(locationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Add single user to specific location
   * @param {number} locationId Location ID where to add a user
   * @param {number} userId User ID to add
   * @param {number} [access] Location access level
   * @param {number} [category] Location notification category
   * @returns {Promise}
   */
  this.addLocationUser = (locationId, userId, access, category) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    if (!userId || userId < 1 || isNaN(userId)) {
      return $q.reject('User ID is not specified!');
    }

    let usersModel = {
      'users': [
        {
          'id': userId,
          'locationAccess': access || 30,
          'category': category || 0
        }
      ]
    };

    return $q.when(webcore.services.location.addLocationUsers(locationId, usersModel)
      .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Update single Location User
   * @param {number} locationId Location ID
   * @param {number} userId User ID to update
   * @param {number} [access] Location access level
   * @param {number} [category] Location notification category
   * @param {boolean} [temp] Temporary access
   * @param {number} [locationRole] Location user role
   * @param {number} [callOrder] Emergency call order
   * @returns {Promise}
   */
  this.updateLocationUser = (locationId, userId, access, category, temp, locationRole, callOrder) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    if (!userId || userId < 1 || isNaN(userId)) {
      return $q.reject('User ID is not specified!');
    }

    let userModel = {
      id: userId
    };
    if (access && !isNaN(access) && access > 0) {
      userModel.locationAccess = access;
    }
    if (category && !isNaN(access)) {
      userModel.category = category;
    }
    if (temp) {
      userModel.temporary = true;
    } else {
      userModel.temporary = false;
    }
    if (locationRole != null) {
      userModel.role = locationRole;
    }
    if (callOrder != null) {
      userModel.callOrder = callOrder;
    }

    return $q.when(webcore.services.location.updateLocationUser(locationId, { user: userModel })
      .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Remove single user from specific location
   * @param {number} locationId ID of the location to remove user from
   * @param {number} userId ID of user to remove
   * @returns {Promise}
   */
  this.removeLocationUser = (locationId, userId) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    if (!userId || userId < 1 || isNaN(userId)) {
      return $q.reject('User ID is not specified!');
    }

    return $q.when(
      webcore.services.location.deleteLocationUser(locationId, userId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets Narratives at location
   * @param {number} locationId
   * @param {Object} [params]
   * @param {string} [sortBy] Property to sort narratives list by.
   * @param {string} [sortOrder] Sort order parameter (desc or asc).
   * @returns {Promise}
   */
  this.getNarratives = (locationId, params, sortBy = 'narrativeDateMs', sortOrder = 'desc') => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    if (!params) {
      params = {};
    }
    params.sortCollection = 'narratives';
    params.sortBy = sortBy;
    params.sortOrder = sortOrder;
    params.rowCount = params.rowCount || 100;

    return $q.when(
      webcore.services.narrative.getNarratives(locationId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Updates Narrative at location
   * @param {number} locationId
   * @param {number} scope
   * @param {Object} narrative
   * @param {number} narrativeId
   * @param {string} [narrativeTime]
   */
  this.updateNarrative = (locationId, scope, narrative, narrativeId, narrativeTime) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    if (!narrativeId || narrativeId < 1 || isNaN(narrativeId)) {
      return $q.reject('Narrative ID is not specified!');
    }
    if (!narrativeTime) {
      return $q.reject('NarrativeTime is not specified!');
    }

    return $q.when(
      webcore.services.narrative.updateNarrative(locationId, scope, narrative, narrativeId, narrativeTime)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Remove Narrative at the loacation
   * By default it removes rganizational narrative (scope 2)
   * @param {number} locationId
   * @param {number} narrativeId
   * @param {string} [narrativeTime]
   * @param {number} [scope] 1 - Location, 2 - Organization
   * @returns {Promise}
   */
  this.deleteNarrative = (locationId, narrativeId, narrativeTime, scope = 2) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    if (!narrativeId || narrativeId < 1 || isNaN(narrativeId)) {
      return $q.reject('Narrative ID is not specified!');
    }
    if (!narrativeTime) {
      return $q.reject('NarrativeTime is not specified!');
    }
    let narrative = {
      status: 1
    };

    return this.updateNarrative(locationId, scope, narrative, narrativeId, narrativeTime);
  };

  /**
   * Get state(s) of specified location
   * @param {number} locationId Location ID.
   * @param {string|string[]} [stateName] State name, multiple values supported.
   * @returns {Promise}
   */
  this.getLocationState = (locationId, stateName) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    return $q.when(
      webcore.services.location.getLocationState(locationId, stateName)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Subscribe for particular location state changes
   * @param {number} locationId Location ID.
   * @param {string} locationStateName State name
   * @returns {WsSubscription}
   */
  this.subscribeForLocationStates = (locationId, locationStateName) => {
    return webcore.services.location.subscribeForLocationStates(locationId, locationStateName);
  };

  /**
   * Set location state
   * The state value can be an any valid JSON node. It can be a single value node (string, integer, etc) or an array or an object node {}.
   * To remove a location state set the value to null.
   * If the value is an object node, the API will read the current state value and try to update only changed fields.
   * To delete a field from the current value set it to null.
   * @param {number} locationId Location ID.
   * @param {string} [stateName] State name.
   * @param {{value: Object}} value any valid JSON node - string, integer, boolean, array, object, etc.
   * @returns {Promise}
   */
  this.setLocationState = (locationId, stateName, value) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    return $q.when(
      webcore.services.location.setLocationState(locationId, stateName, value)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Get time-based state(s) for specified location
   * @param {number} locationId Location ID.
   * @param {string|string[]} [stateName] State name, multiple values supported.
   * @param {string} startDate Start date timestamp.
   * @param {string} [endDate] Optional. End date timestamp.
   * @returns {Promise}
   */
  this.getLocationTimeState = (locationId, stateName, startDate, endDate) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    return $q.when(
      webcore.services.location.getLocationTimeState(locationId, stateName, startDate, endDate)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Get time state(s) historical data for specified location.
   * See {@link https://iotapps.docs.apiary.io/#reference/locations/location-time-states/get-states}
   *
   * @param {number} locationId Location ID.
   * @param params Requested parameters.
   * @param {string|number} params.startDate Return states with dates greater or equal to this value.
   * @param {string|number} params.endDate Return states with dates less than this value.
   *   If not set, only states with dates exactly equal to startDate will be returned.
   * @param {string|string[]} params.name State name, multiple values supported.
   * @param {string|string[]} [params.field] State field to get or filter by.
   *   Multiple values are required for deeper object level.
   * @param {boolean} [params.keepParent] Use the field parameter only for filtering.
   * @param {number} [params.aggregation] Aggregate field values by 1 = hour, 2 = day, 3 = month, 4 = week
   * @returns {Promise}
   */
  this.getLocationTimeStatesHistory = CacheService.cachePromiseFunc((locationId, params) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    return $q.when(
      webcore.services.location.getLocationTimeStatesHistory(locationId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  });

  /**
   * Get Location scenes history. Return location scene change history in backward order (latest first).
   * @param {number} locationId Location ID.
   * @param {string} [startDate] Optional. Start date to begin receiving data.
   * @param {string} [endDate] Optional. End date to stop receiving data. Default is the current date.
   * @returns {Promise}
   */
  this.getLocationScenesHistory = (locationId, startDate, endDate) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    return $q.when(
      webcore.services.location.getLocationScenesHistory(locationId, startDate, endDate)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Set location scene/mode/event.
   * @param {number} locationId Location ID.
   * @param {string} scene location scene/mode/event to set.
   * @returns {Promise}
   */
  this.setLocationScene = (locationId, scene) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    if (!scene) {
      return $q.reject('Location scene is not specified!');
    }
    return $q.when(
      webcore.services.location.setLocationScene(locationId, scene)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Returns location spaces.
   * @param {number} locationId Location ID.
   * @returns {Promise}
   */
  this.getLocationSpaces = (locationId) => {
    return $q.when(
      webcore.services.location.getLocationSpaces(locationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Add new location space.
   * @param {number} locationId
   * @param {number} spaceType
   * @param {string} spaceName
   * @returns {Promise}
   */
  this.addLocationSpace = (locationId, spaceType, spaceName) => {
    if (isNaN(spaceType)) {
      return $q.reject('Space type ID is incorrect!');
    }
    if (!spaceName) {
      return $q.reject('Space name could not be blank!');
    }
    let spaceModel = {
      space: {
        type: spaceType,
        name: spaceName
      }
    };

    return $q.when(
      webcore.services.location.addLocationSpace(locationId, spaceModel)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Get Location priorities history.
   * Return location priority change history in backward order (latest first).
   * @param {number} locationId Location ID.
   * @param {Object} params Request parameters.
   * @param {string} params.startDate Start date to begin receiving data.
   * @param {string} [params.endDate] End date to stop receiving data. Default is the current date.
   * @param {number} [params.priority] Filter by priority, starting from to highest.
   * @param {number} [params.rowCount] Maximum results to get.
   * @returns {Promise}
   */
  this.getLocationPriorityHistory = (locationId, params) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    if (!params || !params.startDate) {
      return $q.reject('No start date specified!');
    }

    // Delete undefined values
    params = angular.copy(params);
    Object.keys(params)
      .forEach(key => params[key] == null && delete params[key]);

    // Sort by date
    params.sortCollection = 'priorities';
    params.sortBy = 'changeDateMs';
    params.sortOrder = 'desc';

    return $q.when(
      webcore.services.location.getLocationPriorityHistory(locationId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Assign relative sub-location.
   * @param {number} locationId Location ID.
   * @param {number} subLocationId Sub-location ID.
   * @returns {Promise}
   */
  this.addSubLocation = (locationId, subLocationId) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    if (!subLocationId || subLocationId < 1 || isNaN(subLocationId)) {
      return $q.reject('Sub-location ID is not specified!');
    }

    return $q.when(
      webcore.services.location.addSubLocation(locationId, subLocationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Remove relative sub-location.
   * @param {number} locationId Location ID.
   * @param {number} subLocationId Sub-location ID.
   * @returns {Promise}
   */
  this.removeSubLocation = (locationId, subLocationId) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    if (!subLocationId || subLocationId < 1 || isNaN(subLocationId)) {
      return $q.reject('Sub-location ID is not specified!');
    }
    return $q.when(
      webcore.services.location.deleteSubLocation(locationId, subLocationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

}
